import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useInvoicePayments,
  InvoicePayment,
} from '../../client-invoice-payments/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentPaymentsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentPayments: React.FC<RecentPaymentsProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { invoicePayments = [], isLoading } = useInvoicePayments();
  const arr = Array.isArray(invoicePayments) ? invoicePayments : [];

  const items = React.useMemo(() => {
    return [...arr]
      .sort((a: InvoicePayment, b: InvoicePayment) => {
        const da = new Date(a.paymentDate || 0).getTime();
        const db = new Date(b.paymentDate || 0).getTime();
        return db - da;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const formatCurrency = (v?: number) =>
    v != null ? `€${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  return (
    <GenericListWidget<InvoicePayment>
      title="Recent Payments"
      viewAllPath="/invoice-payments"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No recent payments"
      getItemKey={(p) => p.paymentId}
      embedded={embedded}
      renderItem={(payment) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/invoice-payments/${payment.paymentId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' &&
            navigate(`/invoice-payments/${payment.paymentId}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {payment.invoice?.InvoiceNo && `Invoice ${payment.invoice.InvoiceNo}`}
              {payment.invoice?.client?.clientName &&
                ` · ${payment.invoice.client.clientName}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {formatCurrency(payment.paymentAmount)}
              {payment.paymentDate &&
                ` · ${new Date(payment.paymentDate).toLocaleDateString('en-US')}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentPayments;
